// 로그인 화면 — P0 판매 진입(docs/p0-sales-entry-spec.md §6) 개선.
//   · 공개 상세페이지와 동일한 제품 identity(--mpx-* 토큰, public-entry.css) — 로그인 기능 계약(GSI initialize →
//     renderButton → callback → AuthContext.login → 서버 auth-verify)은 그대로.
//   · GSI script(index.html async defer)·client ID 준비를 GSI_INIT_TIMEOUT_MS 안에서만 기다린다 — 초과 시 고정
//     오류 + 다시 시도(새로고침). 무한 "로그인 준비 중..." 없음.
//   · Google/서버가 돌려준 원시 오류·credential·token은 화면·console에 싣지 않는다(고정 문구만).
//   · OAuth Authorized JavaScript origins(Google Cloud Console)는 코드 밖 production gate — 여기서 다루지 않는다.
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Clock,
  ExternalLink,
  RefreshCw,
  ShieldCheck,
  TriangleAlert,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext.jsx";
// Session tranche A+B: GIS initialize·콜백은 브로커가 단독 소유한다(gisSilentRefresh.js).
//  로그인 화면은 자기 콜백을 등록만 하고, 버튼 렌더만 직접 한다 — 갱신/재인증 요청과 콜백을 다투지 않는다.
import {
  ensureGisInitialized,
  registerLoginCredentialHandler,
} from "../utils/gisSilentRefresh.js";
import "../styles/public-entry.css";

const CONTACT_URL = "https://www.opinno.co.kr/Contact";
export const GSI_INIT_TIMEOUT_MS = 8000;
const GSI_POLL_MS = 200;
const ICON_SM = { size: 18, strokeWidth: 1.75, "aria-hidden": true };

// 고정 문구 — 원시 오류 문자열을 섞지 않는다.
const MESSAGES = {
  config:
    "로그인 설정(Google 클라이언트 ID)이 준비되지 않았습니다. 관리자에게 문의하세요.",
  timeout:
    "Google 로그인 버튼을 불러오지 못했습니다. 네트워크 연결을 확인한 뒤 다시 시도하세요.",
  "init-failed": "Google 로그인 버튼을 표시하지 못했습니다. 다시 시도하세요.",
  denied:
    "로그인을 완료하지 못했습니다. opinno.co.kr 계정인지 확인한 뒤 다시 시도하세요.",
};
const RETRYABLE = new Set(["timeout", "init-failed"]);

export default function LoginPage() {
  const { login, sessionExpired, sessionUnavailable, retrySession } = useAuth();
  const btnRef = useRef(null);
  // 최신 login 참조 — AuthProvider 재렌더마다 GSI를 재초기화하지 않기 위해 ref 경유
  const loginRef = useRef(login);
  loginRef.current = login;
  // phase: init(준비 중) | ready | config | timeout | init-failed
  const [phase, setPhase] = useState("init");
  const [loginFailed, setLoginFailed] = useState(false);
  const [loginBusy, setLoginBusy] = useState(false);
  const busyRef = useRef(false);

  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
    if (!clientId) {
      setPhase("config");
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    const startedAt = Date.now();
    // 브로커에 로그인 화면 수신자 등록(unmount 시 해제) — GIS 콜백 자체는 브로커 소유 1개뿐
    const unregister = registerLoginCredentialHandler(async (credential) => {
      if (cancelled || busyRef.current) return;
      busyRef.current = true;
      setLoginBusy(true);
      setLoginFailed(false);
      try {
        const result = await loginRef.current(credential);
        if (!cancelled && !result?.success) setLoginFailed(true);
      } catch { if (!cancelled) setLoginFailed(true); }
      finally { busyRef.current = false; if (!cancelled) setLoginBusy(false); }
    });

    const tick = () => {
      if (cancelled) return;
      const gsi = window.google?.accounts?.id;
      if (!gsi) {
        if (Date.now() - startedAt >= GSI_INIT_TIMEOUT_MS) {
          setPhase("timeout");
          return;
        }
        timer = setTimeout(tick, GSI_POLL_MS);
        return;
      }
      try {
        ensureGisInitialized({ google: window.google, clientId });
        if (btnRef.current) {
          // 컨테이너 자식은 GSI 소유(React 비관리) — 재초기화 시 중복 버튼 방지
          btnRef.current.replaceChildren();
          gsi.renderButton(btnRef.current, {
            theme: "outline",
            size: "large",
            text: "signin_with",
            shape: "rectangular",
            width: 300,
          });
        }
        setPhase("ready");
      } catch {
        setPhase("init-failed");
      }
    };
    tick();
    return () => {
      cancelled = true;
      unregister();
      if (timer) clearTimeout(timer);
    };
  }, []);

  const errorKey = loginFailed ? "denied" : MESSAGES[phase] ? phase : null;

  return (
    <div className="pe-page pe-login" data-testid="login-page">
      <div className="pe-container pe-login-top">
        <span className="pe-mono" aria-hidden="true">
          M
        </span>
        <Link to="/" className="pe-login-home" data-testid="login-home-link">
          MEDIAPLEX 홈으로
        </Link>
      </div>
      <div className="pe-container pe-login-body">
        <section className="pe-login-card" aria-labelledby="pe-login-title">
          <span className="pe-login-kicker">
            <ShieldCheck {...ICON_SM} />
            opinno.co.kr 계정 전용
          </span>
          <h1 id="pe-login-title">OPINNO 운영자 로그인</h1>
          <p className="pe-login-desc">
            OPINNO가 발급한 <strong>opinno.co.kr</strong> Google 계정으로
            로그인합니다. 다른 도메인의 계정으로는 로그인할 수 없습니다.
          </p>
          <div className="pe-login-client-help" data-testid="client-access-help">
            <strong>광고주 접속 안내</strong>
            <span>
              Google 로그인이 아니라 담당자가 전달한 전용 공유 링크를 사용하세요.
              링크를 분실했다면 담당 AE에게 재발급을 요청하세요.
            </span>
          </div>
          {sessionUnavailable && <div className="pe-login-notice pe-login-notice--warn" role="status">
            <div className="pe-login-notice-body"><span>로그인 상태를 확인할 서버에 연결하지 못했습니다. 연결이 복구되면 다시 확인해 주세요.</span>
              <button type="button" className="pe-btn pe-btn--ghost pe-btn--sm" onClick={retrySession}>로그인 상태 다시 확인</button>
            </div>
          </div>}
          {loginBusy && <p className="pe-login-status" role="status">계정을 확인하고 있습니다…</p>}
          {sessionExpired && (
            <div className="pe-login-notice pe-login-notice--warn" role="status">
              <Clock {...ICON_SM} />
              <div className="pe-login-notice-body">
                세션이 만료되어 다시 로그인이 필요합니다.
              </div>
            </div>
          )}
          <div
            ref={btnRef}
            inert={loginBusy ? "" : undefined}
            className="pe-gsi"
            data-testid="gsi-button"
            aria-label="Google 로그인"
          />
          {phase === "init" && (
            <p
              className="pe-login-status"
              data-testid="login-status"
              aria-live="polite"
            >
              로그인 준비 중...
            </p>
          )}
          {errorKey && (
            <div
              className="pe-login-notice pe-login-notice--error"
              role="alert"
              data-testid="login-error"
            >
              <TriangleAlert {...ICON_SM} />
              <div className="pe-login-notice-body">
                <span>{MESSAGES[errorKey]}</span>
                {RETRYABLE.has(errorKey) && (
                  <button
                    type="button"
                    className="pe-btn pe-btn--ghost pe-btn--sm"
                    data-testid="login-retry"
                    onClick={() => window.location.reload()}
                  >
                    <RefreshCw {...ICON_SM} />
                    다시 시도
                  </button>
                )}
              </div>
            </div>
          )}
          <p className="pe-login-foot">
            신규 도입 문의는{" "}
            <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer">
              OPINNO 공식 문의 페이지
              <ExternalLink size={14} strokeWidth={1.75} aria-hidden="true" />
              <span className="pe-sr-only">(새 탭에서 열림)</span>
            </a>
            에서 받습니다.
          </p>
        </section>
      </div>
    </div>
  );
}
